import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Modal,
    Button,
    Tooltip,
    Dropdown,
    Avatar,
    Typography,
    Space,
    message,
} from 'antd';
import {
    LeftOutlined,
    RightOutlined,
    FileOutlined,
    CloseOutlined,
    CloudDownloadOutlined,
    PrinterOutlined,
    CopyOutlined,
    ExportOutlined,
    MoreOutlined,
} from '@ant-design/icons';
import { downloadFileViaAnchor } from '@utils/downloadViaAnchor.util';

/**
 * @typedef {{ displayUrl: string, downloadUrl: string, name?: string, variant: 'image' | 'video' | 'pdf' | 'text' | 'file' }} AntFilePreviewItem
 */

/** @typedef {{ senderName?: string, timeLabel?: string, senderAvatarUrl?: string, initials?: string }} AntFilePreviewContextMeta */

const SHELL_BG = '#121212';
const HEADER_BG = '#141414';
const RAIL_BG = '#1a1a1a';
const CONTENT_CANVAS = '#d9d9d9';

const PDF_PAGE_STRIP_MAX = 24;

const navBtnStyle = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 6,
    width: 44,
    height: 44,
    borderRadius: 999,
    border: '1px solid rgba(255,255,255,0.14)',
    background: 'rgba(15,23,42,0.72)',
    color: 'rgba(255,255,255,0.92)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    backdropFilter: 'blur(10px)',
};

function titleSubtitleForVariant(variant) {
    switch (variant) {
        case 'image':
            return { sub: 'Image preview' };
        case 'video':
            return { sub: 'Video preview' };
        case 'pdf':
            return { sub: 'Document preview' };
        case 'text':
            return { sub: 'Text preview' };
        default:
            return { sub: 'Open in a new tab or download' };
    }
}

function PreviewModalTitle({ contextMeta, fileName, sub, index, count }) {
    const showGalleryCount = count > 1;
    if (contextMeta?.senderName || contextMeta?.timeLabel) {
        const initials = (contextMeta.initials || '?').slice(0, 2).toUpperCase();
        return (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 16,
                    width: '100%',
                    paddingRight: 8,
                }}
            >
                <Space align="start" size={12} style={{ minWidth: 0 }}>
                    <Avatar
                        size={40}
                        src={contextMeta.senderAvatarUrl || undefined}
                        style={{
                            backgroundColor: '#4f46e5',
                            flexShrink: 0,
                        }}
                    >
                        {!contextMeta.senderAvatarUrl ? initials : null}
                    </Avatar>
                    <div style={{ minWidth: 0 }}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                flexWrap: 'wrap',
                                marginBottom: 2,
                            }}
                        >
                            <Typography.Text strong style={{ color: '#fff', fontSize: 15 }}>
                                {contextMeta.senderName || '—'}
                            </Typography.Text>
                            {contextMeta.timeLabel ? (
                                <Typography.Text style={{ color: 'rgba(255,255,255,0.45)', fontSize: 13 }}>
                                    {contextMeta.timeLabel}
                                </Typography.Text>
                            ) : null}
                        </div>
                        <Typography.Text
                            ellipsis={{ tooltip: fileName }}
                            style={{
                                color: 'rgba(255,255,255,0.88)',
                                fontSize: 13,
                                display: 'block',
                                maxWidth: 'min(70vw, 820px)',
                            }}
                        >
                            {fileName}
                        </Typography.Text>
                    </div>
                </Space>
                {showGalleryCount ? (
                    <Typography.Text style={{ color: 'rgba(255,255,255,0.5)', flexShrink: 0, fontSize: 13 }}>
                        {index + 1} / {count}
                    </Typography.Text>
                ) : null}
            </div>
        );
    }

    return (
        <div style={{ width: '100%', paddingRight: 8 }}>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 12,
                }}
            >
                <Typography.Text
                    strong
                    ellipsis={{ tooltip: fileName }}
                    style={{ color: '#fff', fontSize: 15, flex: 1, minWidth: 0 }}
                >
                    {fileName}
                </Typography.Text>
                {showGalleryCount ? (
                    <Typography.Text style={{ color: 'rgba(255,255,255,0.5)', flexShrink: 0, fontSize: 13 }}>
                        {index + 1} / {count}
                    </Typography.Text>
                ) : null}
            </div>
            <Typography.Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>{sub}</Typography.Text>
        </div>
    );
}

function PdfPageRail({ page, onPageChange }) {
    const pages = useMemo(() => Array.from({ length: PDF_PAGE_STRIP_MAX }, (_, i) => i + 1), []);
    return (
        <div
            style={{
                width: 96,
                flexShrink: 0,
                background: RAIL_BG,
                borderRight: '1px solid rgba(255,255,255,0.08)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '12px 0',
                gap: 10,
                overflowY: 'auto',
                overflowX: 'hidden',
            }}
        >
            <Typography.Text style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, letterSpacing: 0.6 }}>
                PAGES
            </Typography.Text>
            {pages.map((p) => {
                const active = p === page;
                return (
                    <button
                        key={p}
                        type="button"
                        onClick={() => onPageChange(p)}
                        style={{
                            border: 'none',
                            padding: 0,
                            margin: 0,
                            background: 'transparent',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 6,
                            width: 72,
                        }}
                    >
                        <div
                            style={{
                                width: 56,
                                height: 72,
                                borderRadius: 6,
                                background: active
                                    ? 'linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)'
                                    : 'linear-gradient(180deg, #3f3f46 0%, #27272a 100%)',
                                boxShadow: active
                                    ? '0 0 0 2px #ec4899, 0 4px 14px rgba(0,0,0,0.35)'
                                    : 'inset 0 0 0 1px rgba(255,255,255,0.08)',
                            }}
                        />
                        <Typography.Text
                            style={{
                                fontSize: 12,
                                fontWeight: active ? 700 : 500,
                                color: active ? '#fda4af' : 'rgba(255,255,255,0.55)',
                            }}
                        >
                            {p}
                        </Typography.Text>
                    </button>
                );
            })}
        </div>
    );
}

function FloatingPreviewToolbar({
    displayUrl,
    downloadUrl,
    fileLabel,
    onPrint,
    onCopyLink,
}) {
    const openUrl = displayUrl || downloadUrl;
    const saveUrl = downloadUrl || displayUrl;

    const onMoreMenuClick = useCallback(
        ({ key }) => {
            if (key === 'tab' && openUrl) window.open(openUrl, '_blank', 'noopener,noreferrer');
            if (key === 'dl' && saveUrl) downloadFileViaAnchor(saveUrl, fileLabel);
            if (key === 'copy') onCopyLink?.();
        },
        [openUrl, saveUrl, fileLabel, onCopyLink],
    );

    if (!openUrl && !saveUrl) return null;

    const btnProps = {
        type: 'text',
        size: 'middle',
        style: { color: 'rgba(255,255,255,0.88)' },
    };

    return (
        <Space
            size={4}
            style={{
                position: 'absolute',
                right: 24,
                bottom: 24,
                zIndex: 30,
                padding: '6px 10px',
                borderRadius: 12,
                background: 'rgba(28,28,30,0.94)',
                border: '1px solid rgba(255,255,255,0.12)',
                boxShadow: '0 12px 40px rgba(0,0,0,0.45)',
                backdropFilter: 'blur(12px)',
            }}
        >
            {saveUrl ? (
                <Tooltip title="Download">
                    <Button
                        {...btnProps}
                        icon={<CloudDownloadOutlined />}
                        aria-label="Download"
                        onClick={() => downloadFileViaAnchor(saveUrl, fileLabel)}
                    />
                </Tooltip>
            ) : null}
            <Tooltip title="Print">
                <Button
                    {...btnProps}
                    icon={<PrinterOutlined />}
                    aria-label="Print"
                    onClick={() => onPrint?.()}
                />
            </Tooltip>
            <Tooltip title="Copy link">
                <Button
                    {...btnProps}
                    icon={<CopyOutlined />}
                    aria-label="Copy link"
                    onClick={() => onCopyLink?.()}
                />
            </Tooltip>
            {openUrl ? (
                <Tooltip title="Open in new tab">
                    <Button
                        {...btnProps}
                        icon={<ExportOutlined />}
                        aria-label="Open in new tab"
                        onClick={() => window.open(openUrl, '_blank', 'noopener,noreferrer')}
                    />
                </Tooltip>
            ) : null}
            <Dropdown
                menu={{
                    items: [
                        { key: 'tab', label: 'Open in new tab' },
                        { key: 'dl', label: 'Download' },
                        { key: 'copy', label: 'Copy link' },
                    ],
                    onClick: onMoreMenuClick,
                }}
                trigger={['click']}
                placement="topRight"
            >
                <Button {...btnProps} icon={<MoreOutlined />} aria-label="More" />
            </Dropdown>
        </Space>
    );
}

/**
 * Full-screen style file/media preview (PDF, text, video, unsupported files, images) with optional gallery navigation.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {AntFilePreviewItem[]} props.items
 * @param {number} props.index — controlled active index
 * @param {(i: number) => void} props.onIndexChange
 * @param {number} [props.zIndex]
 * @param {AntFilePreviewContextMeta} [props.contextMeta] — sender row (e.g. chat message)
 */
export default function AntFilePreview({
    open,
    onClose,
    items = [],
    index,
    onIndexChange = () => {},
    zIndex = 1100,
    contextMeta = null,
}) {
    const [imgZoom, setImgZoom] = useState(1);
    const [pdfPage, setPdfPage] = useState(1);
    const safeItems = Array.isArray(items) ? items : [];
    const count = safeItems.length;
    const active = count > 0 ? safeItems[Math.min(Math.max(0, index), count - 1)] : null;

    useEffect(() => {
        if (open) setImgZoom(1);
    }, [open, index, active?.displayUrl]);

    useEffect(() => {
        if (open) setPdfPage(1);
    }, [open, active?.displayUrl, active?.variant]);

    const go = useCallback(
        (delta) => {
            if (count < 2) return;
            const next = (index + delta + count) % count;
            onIndexChange?.(next);
        },
        [count, index, onIndexChange],
    );

    useEffect(() => {
        if (!open || count < 2) return;
        const onKey = (e) => {
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                go(-1);
            }
            if (e.key === 'ArrowRight') {
                e.preventDefault();
                go(1);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, count, go]);

    const headerMeta = useMemo(() => {
        if (!active) return { sub: '' };
        const { sub } = titleSubtitleForVariant(active.variant);
        return { sub };
    }, [active]);

    const pdfSrc = useMemo(() => {
        if (!active?.displayUrl || active.variant !== 'pdf') return '';
        const raw = String(active.displayUrl);
        const base = raw.split('#')[0];
        return `${base}#page=${pdfPage}`;
    }, [active?.displayUrl, active?.variant, pdfPage]);

    const handlePrint = useCallback(() => {
        const url = active?.displayUrl;
        if (!url) return;
        const w = window.open(url, '_blank', 'noopener,noreferrer');
        if (w) {
            w.addEventListener('load', () => {
                try {
                    w.print();
                } catch {
                    /* cross-origin viewers may block */
                }
            });
        }
    }, [active?.displayUrl]);

    const handleCopyLink = useCallback(async () => {
        const url = active?.displayUrl || active?.downloadUrl;
        if (!url) return;
        try {
            await navigator.clipboard.writeText(url);
            message.success('Link copied');
        } catch {
            message.error('Could not copy link');
        }
    }, [active?.displayUrl, active?.downloadUrl]);

    if (!open || !active || !active.displayUrl) return null;

    const showGallery = count > 1;
    const { sub } = headerMeta;
    const primaryName = active.name || 'Attachment';
    const showPdfRail = active.variant === 'pdf';

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            centered={false}
            closable
            closeIcon={<CloseOutlined style={{ color: 'rgba(255,255,255,0.85)', fontSize: 16 }} />}
            width="100%"
            style={{ top: 0, paddingBottom: 0, maxWidth: '100vw' }}
            zIndex={zIndex}
            destroyOnClose
            motion={{ motionName: 'zoom', maskMotionName: 'fade' }}
            styles={{
                content: {
                    margin: 0,
                    padding: 0,
                    maxWidth: '100vw',
                    width: '100vw',
                    height: '100vh',
                    borderRadius: 0,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    background: SHELL_BG,
                    border: 'none',
                },
                header: {
                    flexShrink: 0,
                    margin: 0,
                    padding: '14px 20px',
                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                    background: HEADER_BG,
                },
                body: {
                    flex: 1,
                    minHeight: 0,
                    padding: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    background: SHELL_BG,
                    position: 'relative',
                },
                mask: {
                    background: 'rgba(2,6,23,0.75)',
                    backdropFilter: 'blur(10px)',
                },
            }}
            title={
                <PreviewModalTitle
                    contextMeta={contextMeta}
                    fileName={primaryName}
                    sub={sub}
                    index={index}
                    count={count}
                />
            }
        >
            {showGallery ? (
                <>
                    <button type="button" aria-label="Previous" onClick={() => go(-1)} style={{ ...navBtnStyle, left: 16 }}>
                        <LeftOutlined />
                    </button>
                    <button type="button" aria-label="Next" onClick={() => go(1)} style={{ ...navBtnStyle, right: 16 }}>
                        <RightOutlined />
                    </button>
                </>
            ) : null}

            <div style={{ flex: 1, display: 'flex', minHeight: 0, position: 'relative' }}>
                {showPdfRail ? <PdfPageRail page={pdfPage} onPageChange={setPdfPage} /> : null}

                <div
                    style={{
                        flex: 1,
                        minWidth: 0,
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        background: CONTENT_CANVAS,
                    }}
                >
                    {active.variant === 'pdf' ? (
                        <iframe
                            title={active.name || 'PDF'}
                            src={pdfSrc}
                            style={{
                                flex: 1,
                                minHeight: 0,
                                width: '100%',
                                border: 'none',
                                background: '#525659',
                            }}
                        />
                    ) : null}

                    {active.variant === 'text' ? (
                        <iframe
                            title={active.name || 'Text'}
                            src={active.displayUrl}
                            style={{
                                flex: 1,
                                minHeight: 0,
                                width: '100%',
                                border: 'none',
                                background: '#fff',
                            }}
                        />
                    ) : null}

                    {active.variant === 'file' ? (
                        <div
                            style={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 40,
                                textAlign: 'center',
                                color: 'rgba(0,0,0,0.55)',
                                background: '#f1f5f9',
                            }}
                        >
                            <FileOutlined style={{ fontSize: 48, marginBottom: 16, opacity: 0.35 }} />
                            <Typography.Title level={4} style={{ margin: 0, color: 'rgba(15,23,42,0.88)' }}>
                                Preview isn&apos;t available here
                            </Typography.Title>
                            <Typography.Paragraph style={{ marginTop: 12, maxWidth: 400, color: 'rgba(15,23,42,0.6)' }}>
                                Spreadsheets and other office files open best in a new tab or via download.
                            </Typography.Paragraph>
                        </div>
                    ) : null}

                    {active.variant === 'video' ? (
                        <div
                            style={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 24,
                                background: '#0f0f10',
                            }}
                        >
                            <video
                                src={active.displayUrl}
                                controls
                                autoPlay
                                playsInline
                                style={{ width: '100%', maxHeight: '100%', objectFit: 'contain' }}
                            />
                        </div>
                    ) : null}

                    {active.variant === 'image' ? (
                        <div style={{ flex: 1, position: 'relative', background: '#0f0f10', minHeight: 0 }}>
                            <div
                                style={{
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: 24,
                                }}
                            >
                                <img
                                    src={active.displayUrl}
                                    alt={active.name || ''}
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '100%',
                                        objectFit: 'contain',
                                        transform: `scale(${imgZoom})`,
                                        transformOrigin: 'center center',
                                    }}
                                />
                            </div>
                            <Space
                                size={6}
                                style={{
                                    position: 'absolute',
                                    left: '50%',
                                    bottom: 88,
                                    transform: 'translateX(-50%)',
                                    padding: '6px 10px',
                                    borderRadius: 999,
                                    border: '1px solid rgba(255,255,255,0.14)',
                                    background: 'rgba(15,23,42,0.65)',
                                    backdropFilter: 'blur(10px)',
                                    zIndex: 25,
                                }}
                            >
                                <Button
                                    type="text"
                                    size="small"
                                    style={{ color: '#fff' }}
                                    onClick={() =>
                                        setImgZoom((z) => Math.max(0.5, Math.round((z - 0.1) * 10) / 10))
                                    }
                                >
                                    −
                                </Button>
                                <Typography.Text style={{ color: '#fff', minWidth: 48, textAlign: 'center', fontSize: 12 }}>
                                    {Math.round(imgZoom * 100)}%
                                </Typography.Text>
                                <Button
                                    type="text"
                                    size="small"
                                    style={{ color: '#fff' }}
                                    onClick={() =>
                                        setImgZoom((z) => Math.min(2.5, Math.round((z + 0.1) * 10) / 10))
                                    }
                                >
                                    +
                                </Button>
                            </Space>
                        </div>
                    ) : null}

                    <FloatingPreviewToolbar
                        displayUrl={active.displayUrl}
                        downloadUrl={active.downloadUrl}
                        fileLabel={active.name}
                        onPrint={handlePrint}
                        onCopyLink={handleCopyLink}
                    />
                </div>
            </div>
        </Modal>
    );
}
